import { AppNotificationType } from "@prisma/client";
import { useState } from "react";

import dayjs from "@calcom/dayjs";
import { trpc } from "@calcom/trpc/react";
import { Button, Dropdown, DropdownMenuContent, DropdownMenuTrigger } from "@calcom/ui";
import { BellIcon } from "@calcom/web/components/Icons";

function notificationAccent(type: AppNotificationType) {
  return type === AppNotificationType.PAYMENT_POST_FAILED ? "bg-red-500" : "bg-amber-500";
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const utils = trpc.useContext();
  const { data } = trpc.viewer.appNotification.list.useQuery(
    { limit: 20 },
    { refetchInterval: 60_000, refetchOnWindowFocus: true }
  );

  const markRead = trpc.viewer.appNotification.markRead.useMutation({
    onSuccess: () => {
      void utils.viewer.appNotification.list.invalidate();
    },
  });
  const markAllRead = trpc.viewer.appNotification.markAllRead.useMutation({
    onSuccess: () => {
      void utils.viewer.appNotification.list.invalidate();
    },
  });

  const unreadCount = data?.unreadCount ?? 0;
  const items = data?.items ?? [];

  return (
    <Dropdown open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Notifications"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white !px-0 text-gray-600 hover:bg-white hover:text-gray-600">
          <BellIcon className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" style={{ marginTop: "8px" }}>
        <div className="-mx-1 w-[360px] max-w-[calc(100vw-24px)] overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <p className="text-sm font-medium text-gray-900">Notifications</p>
            {unreadCount > 0 && (
              <button
                type="button"
                className="text-xs font-medium text-blue-700 hover:text-blue-800"
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isLoading}>
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-gray-500">No notifications</p>
            ) : (
              items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-gray-50 ${
                    item.read ? "bg-white" : "bg-blue-50/60"
                  }`}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => {
                    if (!item.read) markRead.mutate({ id: item.id });
                  }}>
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notificationAccent(item.type)}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{item.title}</span>
                    <span className="mt-0.5 block text-xs text-gray-600">{item.message}</span>
                    <span className="mt-1 block text-[11px] text-gray-400">
                      {item.office.name} · {dayjs(item.createdAt).fromNow()}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </Dropdown>
  );
}
